/* eslint-disable no-bitwise */
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { Link } from 'react-router-dom'
import Badge from 'react-bootstrap/Badge'
import Row from 'react-bootstrap/Row'
import Card from 'react-bootstrap/Card'
import NavLink from 'react-bootstrap/NavLink'
import Accordion from 'react-bootstrap/Accordion'
import Nav from 'react-bootstrap/Nav'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import { Button, Popover, Stack } from 'react-bootstrap'
import { ReactComponent as CheckCircle } from '../assets/svgs/checkCircleFill.svg'
import { ReactComponent as Building } from '../assets/svgs/building.svg'
import { ReactComponent as ExclamationCircle } from '../assets/svgs/exclamationCircleFill.svg'
import { ReactComponent as RefreshCw } from '../assets/svgs/refresh-cw.svg'
import { ReactComponent as Logo } from '../assets/svgs/logo.svg'
import { ReactComponent as Monitor } from '../assets/svgs/monitor.svg'
import UploadIcon from '../assets/images/upload-icon.png'
import { ReactComponent as Calendar } from '../assets/svgs/calendar.svg'
import BaseButton from './buttons/BaseButton'
import { CustomLogo } from '../assets/svgs/customLogo'
import ActivePositions from '../assets/svgs/activePositions'
import Organizations from '../assets/svgs/organizations'

export const StyledTab = styled(NavLink)(
  ({ color, active, fontSize = '15px', theme }) => ({
    color: `${color} !important`,
    fontSize,
    fontFamily: 'PoppinsMedium',
    textDecoration: 'none',
    backgroundColor: 'white !important',
    padding: '5px 15px',
    minWidth: '120px',
    textAlign: 'center',
    boxShadow: `0 4px 8px 0 rgba(0, 0, 0, 0.1)`,
    borderBottom: !active
      ? `5px solid ${theme.brandColorCreamy} !important`
      : 'none !important',
    '&:active': {
      color: `${theme.brandColorLightOrange} !important`,
      borderBottom: `3px solid ${theme.black}!important`,
    },
    '&:hover': {
      color: `${theme.brandColorLightOrange} !important`,
    },
  }),
)

export const CustomStyledTab = styled('div')(
  ({ active, fontSize, theme, size = 'md' }) => {
    const sizeStyles = {
      sm: {
        fontSize: '14px',
        padding: '4px 12px',
        minWidth: '100px',
      },
      md: {
        fontSize: fontSize || '16px',
        padding: '5px 15px',
        minWidth: '120px',
      },
    }

    const currentSize = sizeStyles[size] || sizeStyles.md

    const activeColor = theme.isLightCustomColor
      ? theme.tabActiveTextColor
      : theme.brandColorLightOrange

    const activeBorderColor = theme.isLightCustomColor
      ? theme.brandColorOrange
      : theme.brandColorLightOrange

    return {
      color: !active ? `${theme.inactive}` : activeColor,
      fontSize: currentSize.fontSize,
      textDecoration: 'none',
      padding: currentSize.padding,
      minWidth: currentSize.minWidth,
      textAlign: 'center',
      borderBottom: !active
        ? `3px solid ${theme.inactive} !important`
        : `3px solid ${activeBorderColor} !important`,
    }
  },
)

export const StyledSettingsSideNav = styled(Nav)(({ theme }) => ({
  backgroundColor: `${theme.sideNavBg}`,
}))

export const StyledSettingsSideNavLink = styled(NavLink)(
  ({ backgroundColor }) => ({
    backgroundColor,
  }),
)

export const HorizontalScroll = styled('div')(() => ({
  display: 'grid',
  gap: '5px',
  gridAutoFlow: 'column',
  gridAutoColumns: '30%',
  overflowX: 'auto',
  overscrollBehaviorInline: 'contain',
  // paddingBottom: '9px',
  // margin: '0 10px',
}))

export const VerticalScroll = styled('div')(({ maxHeight }) => ({
  gridAutoRows: '0%',
  gridAutoFlow: 'row',
  overflowY: 'auto',
  overscrollBehaviorInline: 'auto',
  maxHeight,
}))

export const InfiniteScroll = styled('div')(() => ({
  overflowY: 'auto',
  height: '500px',
}))

export const InteractiveViewHorizontalScroll = styled('div')(() => ({
  gridAutoColumns: '25%',
  gridAutoRows: '0%',
  gridAutoFlow: 'row',
  display: 'flex',
  overflowX: 'auto',
  overflowY: 'hidden',
  height: '555px',
}))

export const Typography = styled('span')(
  ({
    color,
    size,
    bold,
    variant,
    regular,
    backgroundColor = 'transparent',
    display = 'inline-block',
    textDecoration = 'none',
  }) => ({
    color,
    fontSize: size,
    // eslint-disable-next-line no-nested-ternary
    fontFamily: regular
      ? 'PoppinsRegular'
      : bold
        ? 'PoppinsSemiBold'
        : 'PoppinsMedium',
    textDecoration,
    textTransform: variant || 'none',
    backgroundColor,
    display,
  }),
)

export const NoWrapDiv = styled('div')(({ width }) => ({
  overflow: 'hidden',
  width,
  span: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
}))

export const BreadcrumbLink = styled(Link)(
  ({ color = null, variant = 'underline', theme }) => ({
    textDecoration: variant,
    color: color || theme.brandColorOrange,
  }),
)

export const ColoredBadge = styled(Badge)(
  ({
    color = null,
    textDecoration = 'none',
    fontSize,
    width,
    customHeight,
    hoverText = null,
    theme,
  }) => ({
    backgroundColor: `${color || theme.brandColorOrange} !important`,
    textDecoration,
    fontSize,
    width,
    height: customHeight || '20px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: hoverText && 'relative',
    ...(hoverText && {
      '&:hover::after': {
        content: `"${hoverText}"`,
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        color: `${theme.white}`,
        padding: '5px 8px',
        borderRadius: '4px',
        fontSize: '0.75em',
        whiteSpace: 'nowrap',
        zIndex: '10',
      },
    }),
  }),
)

export const TotalBadge = styled(Badge)(({ theme }) => ({
  backgroundColor: `${theme.brandColorOrange} !important`,
  display: 'inline-flex',
  padding: '3px 8px',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '100px',
  margin: '0 5px',
}))

export const StyledBadge = styled('div')(({ color, theme }) => {
  const darkenColor = (bgcolor, percent) => {
    if (bgcolor === `${theme.black}`) return `${theme.white}`
    const num = parseInt(bgcolor.slice(1), 16)
    const amt = Math.round(2.55 * percent)
    const r = ((num >> 16) & 0xff) - amt
    const g = ((num >> 8) & 0xff) - amt
    const b = (num & 0xff) - amt

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

    const clampR = clamp(r, 0, 255)
    const clampG = clamp(g, 0, 255)
    const clampB = clamp(b, 0, 255)

    return `#${((1 << 24) + (clampR << 16) + (clampG << 8) + clampB)
      .toString(16)
      .slice(1)}`
  }

  const bgColor = color || theme.brandColorLightOrange

  return {
    display: 'inline-flex',
    height: '29px',
    padding: '4px 12px',
    margin: '2px',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '12px',
    fontWeight: '500',
    borderRadius: '100px',
    backgroundColor: bgColor,
    color: darkenColor(bgColor, 50),
  }
})

export const Background = styled('div')(({ background }) => ({
  background,
}))

export const CustomContainer = styled(Row)(() => ({
  padding: '0.938rem 0.938rem 0.938rem 0.938rem',
  margin: '0 0 0.938rem 0',
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  boxShadow: '0px 10px 20px #12263F08',
  border: '1px solid #CED4DA',
  borderRadius: '5px',
  opacity: '1',
}))

export const ExternalLink = styled('a')(({ color, variant, decoration }) => ({
  color,
  textDecoration: decoration || 'none',
  textTransform: variant || 'none',
  '&:hover': {
    color,
    textDecoration: 'none',
    textTransform: variant || 'none',
  },
}))

export const InterviewSummaryAccordionHeader = styled(Accordion.Header)(
  ({ display = 'flex' }) => ({
    backgroundColor: 'transparent !important',
    boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '5px',
    '& .accordion-button': {
      boxShadow: 'none !important',
      fontWeight: '500',
      backgroundColor: 'transparent !important',
      color: 'black',
      display,
    },
  }),
)

export const InterviewQuestionAccordionHeader = styled(Accordion.Header)(
  ({ theme }) => ({
    border: 'whitesmoke',
    borderLeft: `6px solid ${theme.brandColorOrange}`,
    fontWeight: 'bold',
    backgroundColor: 'transparent !important',
    boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '5px',
    '& .accordion-button': {
      boxShadow: 'none !important',
      fontWeight: '500',
      backgroundColor: 'transparent !important',
      color: 'black',
    },
  }),
)

export const EmailViewAccordionHeader = styled(Accordion.Header)(
  ({ sideColor }) => ({
    border: 'whitesmoke',
    borderLeft: `6px solid ${sideColor}`,
    fontWeight: 'bold',
    backgroundColor: 'transparent !important',
    boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '5px',
    '& .accordion-button': {
      boxShadow: 'none !important',
      fontWeight: '500',
      backgroundColor: 'transparent !important',
      color: 'black',
    },
  }),
)

export const EmailCard = styled(Card)(({ sideColor, theme }) => ({
  padding: '0.25rem 1rem',
  border: 'whitesmoke',
  borderLeft: `6px solid ${sideColor || theme.brandColorOrange}`,
  fontWeight: 'bold',
  backgroundColor: 'transparent !important',
  boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.1)',
  borderRadius: '5px',
}))

export const FillCheckCircle = styled(CheckCircle)(({ size, color }) => ({
  color,
  width: size,
  height: size,
}))

export const FillExclamationCircle = styled(ExclamationCircle)(
  ({ size, color }) => ({
    color,
    width: size,
    height: size,
  }),
)

export const AlternativeBuildingLogo = styled(Building)(({ size, color }) => ({
  width: size,
  height: size,
  fill: color,
}))

export const GradientBackground = styled('div')(({ background }) => ({
  background,
}))

export const TopBorderCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.white,
  borderRadius: '5px',
  borderTop: `6px solid ${theme.brandColorOrange}`,
}))

export const FlexWidthRow = styled(Row)(({ width }) => ({
  width,
}))

export const QuickLinkButton = styled(BaseButton)(() => ({
  width: '166px',
  height: '124px',
}))

export const CustomModalHeader = styled(Modal.Header)(() => ({
  fontSize: '20px',
  marginRight: '20px',
}))

export const SubscriptionPlanContainer = styled('div')((props) => ({
  width: '300px',
  height: '400px',
  border: `2px solid ${props.borderColor}`,
  borderRadius: '8px',
  padding: '1.5rem',
  textAlign: 'center',
  color: props.borderColor,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}))

export const PlanText = styled('div')(() => ({
  flex: 1,
  textAlign: 'center',
}))

export const ButtonAlign = styled('div')(({ display = 'flex' }) => ({
  display,
  justifyContent: 'flex-end',
}))

export const BillDurationText = styled('p')((props) => ({
  color: props.borderColor,
  border: `1px solid ${props.borderColor}`,
  borderRadius: '4px',
  padding: '4px 8px',
  display: 'inline',
}))

export const StyledInteractiveView = styled('div')(() => ({
  flex: '0 0 25%',
  minWidth: '250px',
}))

export const Div30 = styled('div')(() => ({
  width: '30%',
}))

// Company logo

export const LogoContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

export const LogoInput = styled('input')({
  display: 'none',
})

export const ImgUploadWrap = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '200px',
  height: '200px',
  overflow: 'hidden',
  borderRadius: '50%',
  '&:before': {
    content: '""',
    background: `url(${UploadIcon}) no-repeat center center`,
    backgroundSize: 'cover',
    fontSize: '90px',
    position: 'absolute',
    paddingTop: '80px',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    textAlign: 'center',
    color: theme.brandColorLightOrange,
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    opacity: 0,
    transition: '.5s ease',
    // backgroundColor: '#fff',
  },
  '&:hover:before': {
    opacity: 1,
  },
}))

export const CompanyLogoImg = styled('img')({
  width: 'auto',
  height: '100%',
  objectFit: 'cover',
})

export const CompanyLogoLabel = styled('label')(({ theme }) => ({
  textTransform: 'uppercase',
  fontWeight: 700,
  color: theme.brandColorLightOrange,
  borderRadius: '50%',
  display: 'inline-block',
  position: 'relative',
  padding: '6px',
  cursor: 'pointer',
  backgroundColor: 'transparent',
  marginBottom: '25px',
  boxShadow: '2px 2px 4px 2px rgba(0, 0, 0, 0.1)',
}))

export const NotificationMessagePageBox = styled('div')(
  ({ color, isLoading, theme }) => ({
    backgroundColor: theme.white,
    border: `5px solid ${color}`,
    color,
    minWidth: '30%',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      border: `5px solid ${theme.brandColorLightOrange}`,
      animation: isLoading ? 'clippath 3s infinite linear' : 'none',
    },
    '@keyframes clippath': {
      '0%, 100%': {
        clipPath: 'inset(0 0 95% 0)',
      },
      '25%': {
        clipPath: 'inset(0 95% 0 0)',
      },
      '50%': {
        clipPath: 'inset(95% 0 0 0)',
      },
      '75%': {
        clipPath: 'inset(0 0 0 95%)',
      },
    },
  }),
)

export const TextSeparator = styled('div')({
  display: 'inline',
  marginRight: '5px',
  marginLeft: '5px',
  transform: 'rotate(90deg)',
  transformOrigin: '0 0',
  border: '1px #C8C9CA solid',
})

export const OrgBlockingContainer = styled('div')(() => ({
  width: '100%',
  minHeight: '300px',
  padding: '32px',
  background: 'white',
  boxShadow: '0px 20px 20px rgba(0, 0, 0, 0.08)',
  borderRadius: '8px',
  border: '1px solid #DEE2E6',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

export const OrgBlockingHeader = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
}))

export const OrgBlockingTitle = styled('div')(({ theme }) => ({
  color: theme.darkRed,
  fontSize: '20px',
  fontWeight: 500,
  textTransform: 'capitalize',
  wordWrap: 'break-word',
}))

export const OrgBlockingSection = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '12px',
}))

export const OrgBlockingButtonContainer = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
}))

export const OrgBlockingCancelButton = styled('button')(() => ({
  width: '200px',
  height: '40px',
  padding: '5px 16px',
  borderRadius: '4px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '16px',
  fontWeight: 500,
  textTransform: 'capitalize',
  cursor: 'pointer',
  transition: 'background-color 0.3s, border-color 0.3s',
  border: '1px solid #A0B8C9',
  backgroundColor: 'white',
  color: '#5A5555',

  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
}))

export const OrgBlockingConfirmButton = styled('button')(
  ({ backgroundColor, theme }) => ({
    width: '200px',
    height: '40px',
    padding: '5px 16px',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '16px',
    fontWeight: 500,
    textTransform: 'capitalize',
    cursor: 'pointer',
    transition: 'background-color 0.3s, border-color 0.3s',
    backgroundColor: backgroundColor || theme.darkRed,
    border: `1px solid ${backgroundColor || theme.darkRed}`,
    color: 'white',

    '&:hover': {
      backgroundColor: theme.deepRed,
      borderColor: theme.deepRed,
    },
  }),
)

export const InPersonInterviewSelectedDatesContainer = styled('div')({
  marginTop: '1rem',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
})

export const InPersonInterviewDateBadge = styled('div')(({ theme }) => ({
  display: 'flex',
  fontSize: '12px',
  alignItems: 'center',
  backgroundColor: theme.brandColorOrange,
  color: 'white',
  padding: '0.3rem 0.8rem',
  borderRadius: '30px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  '& .btn-close': {
    backgroundColor: 'transparent',
    marginLeft: '0.3rem',
    padding: '0',
  },
}))

export const InPersonInterviewRadioGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  gap: '1rem',
  marginBottom: '1.5rem',
})

export const InPersonInterviewRadioLabel = styled('label')({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '16px',
  color: '#555',
})

export const DashboardCard = styled(Card)(({ theme }) => ({
  boxShadow: '0px 10px 20px rgba(18, 38, 63, 0.0313726)',
  backgroundColor: theme.white,
  borderRadius: '5px',
}))

export const CanvasBackButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '0.5rem',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: '500',
  color: theme.grayishBrown,
  '&:hover': {
    color: theme.black,
  },
}))

export const IdleAnswerCardStyle = {
  display: 'flex',
  alignItems: 'start',
  justifyContent: 'start',
  marginTop: '10px',
  padding: '10px',
  background: '#f8f9fa',
  border: '1px solid #dee2e6',
  borderRadius: '0.25rem',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
}

export const IdleAnswerTitleStyle = {
  fontSize: '0.95rem',
  fontWeight: 500,
  color: '#495057',
}

export const IdleAnswerValueStyle = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#343a40',
}

export const IconButton = styled.button(({ theme }) => ({
  margin: '6px',
  fontSize: '0.875rem',
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '6px',
  borderRadius: '9999px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  transition: 'background-color 200ms, color 200ms',
  '&:hover': {
    backgroundColor: theme.brandColorOrange,
    color: theme.white,
  },
  '&:focus': {
    outline: 'none',
  },
}))

export const ValueContainer = styled.div({
  position: 'relative',
  width: '48px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'visible',
})

export const NumberDisplay = styled.div(({ theme }) => ({
  width: '40px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.white,
  border: '1px solid #E5E7EB',
  borderRadius: '0.375rem',
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  zIndex: 10,
}))

export const PreviousValue = styled.div({
  position: 'absolute',
  opacity: 0.3,
  color: '#9CA3AF',
  transform: 'translateY(-28px)',
  zIndex: 0,
})

export const NextValue = styled.div({
  position: 'absolute',
  opacity: 0.3,
  color: '#9CA3AF',
  transform: 'translateY(28px)',
  zIndex: 0,
})

export const InPersonSchedularWrapper = styled.div({
  position: 'relative',
  width: '100%',
})

export const InPersonSchedularInputWrapper = styled.div({
  position: 'relative',
  cursor: 'pointer',
})

export const InPersonSchedularInput = styled.input({
  width: '100%',
  padding: '0.5rem',
  paddingRight: '2rem',
  fontSize: '12px',
  border: '1px solid #D1D5DB',
  borderRadius: '0.375rem',
  outline: 'none',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  ':focus': {
    borderColor: '#076698',
    boxShadow: '0 0 0 1px #076698',
  },
})

export const InPersonSchedularCalendarIcon = styled(Calendar)({
  position: 'absolute',
  right: '0.5rem',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#9CA3AF',
  pointerEvents: 'none',
})

export const InPersonSchedularCalendarContainer = styled.div({
  position: 'absolute',
  zIndex: 10,
  marginTop: '0.25rem',
  backgroundColor: '#ffffff',
  borderRadius: '0.5rem',
  boxShadow:
    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
  border: '1px solid #E5E7EB',
  padding: '0.75rem',
  width: '18rem',
})

export const InPersonSchedularCalendarHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '0.5rem',
})

export const InPersonSchedularNavButton = styled.button({
  padding: '0.25rem',
  borderRadius: '9999px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: '#F3F4F6',
  },
})

export const InPersonSchedularMonthYear = styled.div({
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
})

export const TabContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '0.5rem',
})

export const TabButton = styled.button((props) => ({
  padding: '0.5rem 1rem',
  border: 'none',
  backgroundColor: props.active ? '#076698' : 'transparent',
  color: props.active ? '#ffffff' : '#374151',
  cursor: 'pointer',
  borderRadius: '0.375rem',
  margin: '0 0.25rem',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  ':hover': {
    backgroundColor: props.active ? '#076698' : 'rgba(7,102,152,0.1)',
  },
}))

export const InPersonSchedularWeekdaysGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '0.25rem',
  marginBottom: '0.25rem',
})

export const InPersonSchedularWeekdayCell = styled.div({
  height: '2rem',
  width: '2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.75rem',
  color: '#6B7280',
})

export const InPersonSchedularDaysGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '0.25rem',
})

export const InPersonSchedularDayButton = styled.button((props) => ({
  height: '2rem',
  width: '2rem',
  borderRadius: '9999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  transition: 'all 0.2s ease',
  backgroundColor: props.selected
    ? '#076698'
    : props.isToday
      ? 'rgba(7, 102, 152, 0.15)'
      : 'transparent',
  // eslint-disable-next-line no-nested-ternary
  color: props.selected
    ? '#ffffff'
    : props.adjacent
      ? '#A0AEC0'
      : props.isToday
        ? '#076698'
        : '#374151',
  border: props.isToday && !props.selected ? '1px solid #076698' : 'none',
  cursor: 'pointer',
  ':hover': {
    // eslint-disable-next-line no-nested-ternary
    backgroundColor: props.selected
      ? '#076698'
      : props.adjacent
        ? 'rgba(160,174,192,0.2)'
        : 'rgba(7,102,152,0.1)',
  },
}))

export const MonthYearPopoverContainer = styled.div({
  position: 'absolute',
  top: '2.5rem',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: '#ffffff',
  borderRadius: '0.5rem',
  boxShadow:
    '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
  border: '1px solid #E5E7EB',
  padding: '1rem',
  width: '16rem',
  zIndex: 20,
})

export const MonthGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '0.5rem',
  marginBottom: '1rem',
})

export const MonthButton = styled.button((props) => ({
  padding: '0.5rem',
  border: '1px solid transparent',
  borderRadius: '0.375rem',
  backgroundColor: props.selected ? '#076698' : 'transparent',
  color: props.selected ? '#ffffff' : '#374151',
  fontSize: '0.875rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  ':hover': {
    backgroundColor: props.selected ? '#076698' : 'rgba(7,102,152,0.1)',
  },
}))

export const YearGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '0.5rem',
  marginBottom: '1rem',
})

export const YearButtonStyled = styled.button((props) => ({
  padding: '0.5rem',
  border: '1px solid transparent',
  borderRadius: '0.375rem',
  backgroundColor: props.selected ? '#076698' : 'transparent',
  color: props.selected ? '#ffffff' : '#374151',
  fontSize: '0.875rem',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  ':hover': {
    backgroundColor: props.selected ? '#076698' : 'rgba(7,102,152,0.1)',
  },
}))

export const YearGridHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '0.5rem',
})

export const YearDisplay = styled.span({
  fontSize: '0.875rem',
  fontWeight: 500,
})

export const InPersonSchedularModalOptionWrapper = styled.div(
  ({ checked, theme }) => ({
    width: '100%',
    position: 'relative',
    display: 'flex',
    cursor: 'pointer',
    borderRadius: '0.5rem',
    padding: '1rem 1.25rem',
    boxShadow:
      '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    outline: 'none',
    backgroundColor: checked ? theme.headersMiniTitle : 'white',
    color: checked ? 'white' : '#111827',
  }),
)

export const NotFoundContainer = styled.div({
  minHeight: '100vh',
  backgroundColor: '#f9fafb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
  position: 'relative',
})

export const NotFoundContentWrapper = styled.div({
  maxWidth: '48rem',
  width: '100%',
  textAlign: 'center',
})

export const NotFoundLogoContainer = styled.div({
  marginBottom: '2rem',
})

export const NotFoundLogo = styled.svg({
  margin: '0 auto',
})

export const NotFoundErrorCode = styled.h1(({ theme }) => ({
  fontSize: '150px',
  fontWeight: 'bold',
  color: theme.brandColorOrange,
  lineHeight: 1,
}))

export const NotFoundHeading = styled.h2({
  fontSize: '1.875rem',
  fontWeight: '600',
  color: '#1f2937',
  marginBottom: '1rem',
})

export const NotFoundMessage = styled.p({
  color: '#4b5563',
  marginBottom: '2rem',
  maxWidth: '28rem',
  margin: '0 auto 2rem',
})

export const NotFoundButtonContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  justifyContent: 'center',
  '@media (min-width: 640px)': {
    flexDirection: 'row',
  },
})

export const NotFoundPrimaryButton = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 1.5rem',
  backgroundColor: theme.brandColorOrange,
  color: 'white',
  borderRadius: '0.5rem',
  '&:hover': {
    backgroundColor: '#065577',
    transition: 'background-color 200ms ease',
  },
}))

export const NotFoundSecondaryButton = styled.button(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 1.5rem',
  border: '2px solid #076698',
  color: theme.brandColorOrange,
  borderRadius: '0.5rem',
  '&:hover': {
    backgroundColor: '#f9fafb',
    transition: 'background-color 200ms ease',
  },
}))

export const NotFoundHelpText = styled.p({
  marginTop: '2rem',
  color: '#6b7280',
})

export const NotFoundSupportLink = styled.a(({ theme }) => ({
  color: theme.brandColorOrange,
  '&:hover': {
    textDecoration: 'underline',
  },
}))

export const NotFoundBackgroundDecoration = styled.div({
  position: 'absolute',
  inset: 0,
  zIndex: -10,
  overflow: 'hidden',
})

export const NotFoundTopRightCircle = styled.div({
  position: 'absolute',
  top: '-50%',
  right: '-50%',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  backgroundColor: 'rgba(7, 102, 152, 0.05)',
})

export const NotFoundBottomLeftCircle = styled.div({
  position: 'absolute',
  bottom: '-50%',
  left: '-50%',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  backgroundColor: 'rgba(7, 102, 152, 0.05)',
})

export const NotFoundIconWrapper = styled.span(({ dir }) => ({
  marginRight: dir === 'rtl' ? 0 : '0.5rem',
  marginLeft: dir === 'rtl' ? '0.5rem' : 0,
  display: 'inline-flex',
}))

export const ErrorBoundaryContainer = styled.div({
  minHeight: '100vh',
  backgroundColor: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
})

export const ErrorBoundaryMaxWidthWrapper = styled.div({
  maxWidth: '28rem',
  width: '100%',
})

export const ErrorBoundaryErrorCard = styled.div({
  background: 'white',
  borderRadius: '0.5rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  border: '1px solid #f3f4f6',
  overflow: 'hidden',
})

export const ErrorBoundaryHeader = styled.div({
  padding: '1.5rem',
  borderBottom: '1px solid #f3f4f6',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

export const ErrorBoundaryLogo = styled(Logo)({
  width: '3rem',
  height: '3rem',
  marginBottom: '0.75rem',
})

export const ErrorBoundaryHeading = styled.h2({
  fontSize: '1.125rem',
  lineHeight: '1.75rem',
  fontWeight: 600,
  color: '#076698',
})

export const ErrorBoundaryContent = styled.div({
  padding: '1.5rem',
})

export const ErrorBoundaryMessageText = styled.p({
  color: '#4b5563',
  fontSize: '0.875rem',
  lineHeight: '1.5rem',
  textAlign: 'center',
  marginBottom: '1.5rem',
})

export const ErrorBoundaryErrorMessageBox = styled.div({
  marginTop: '1rem',
  padding: '0.75rem',
  backgroundColor: '#f9fafb',
  borderRadius: '0.375rem',
  border: '1px solid #f3f4f6',
})

export const ErrorBoundaryErrorMessageText = styled.p({
  fontSize: '0.875rem',
  color: '#6b7280',
  fontFamily: 'monospace',
})

export const ErrorBoundaryActionsWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
})

export const ErrorBoundaryRetryButton = styled.button({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  backgroundColor: '#076698',
  color: 'white',
  fontSize: '0.875rem',
  fontWeight: 500,
  padding: '0.625rem 1rem',
  border: 'none',
  borderRadius: '0.375rem',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '#065577',
  },
})

export const ErrorBoundaryRefreshIcon = styled(RefreshCw)({
  width: '1rem',
  height: '1rem',
})

export const ErrorBoundaryAdminText = styled.p({
  fontSize: '0.75rem',
  color: '#6b7280',
  textAlign: 'center',
})

export const loadingAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(-100%);
  }
`

export const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`

export const LoadingWrapper = styled.div({
  minHeight: '100vh',
  backgroundColor: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

export const LoadingContent = styled.div({
  textAlign: 'center',
})

export const LoadingLogo = styled(CustomLogo)(({ theme }) => ({
  margin: '0 auto',
  marginBottom: '1.5rem',
  width: '5rem',
  height: '5rem',
  animation: `${pulseAnimation} 2s ease-in-out infinite`,
  '& .logo-part1': {
    fill: theme.brandColorOrange,
  },
  '& .logo-part2': {
    fill: theme.logoFill,
  },
}))

export const LoadingBarContainer = styled.div({
  marginTop: '2rem',
  height: '4px',
  width: '8rem',
  margin: '0 auto',
  backgroundColor: 'rgba(7, 102, 152, 0.2)',
  borderRadius: '9999px',
  overflow: 'hidden',
})

export const LoadingBar = styled.div(({ theme }) => ({
  height: '100%',
  width: '50%',
  backgroundColor: theme.brandColorOrange,
  borderRadius: '9999px',
  animation: `${loadingAnimation} 1s ease-in-out infinite`,
}))

export const ViewPortWarningStyledContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  height: '100%',
})

export const ViewPortWarningStyledCard = styled.div({
  width: '100%',
  maxWidth: '32rem',
  borderRadius: '1.5rem',
  padding: '3rem',
})

export const ViewPortWarningStyledIconContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '2.5rem',
})

export const ViewPortWarningStyledIconRelative = styled.div({
  position: 'relative',
})

export const ViewPortWarningStyledIconBlur = styled.div({
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(7, 102, 152, 0.2)',
  filter: 'blur(20px)',
  borderRadius: '9999px',
})

export const ViewPortWarningStyledIconGradient = styled.div({
  position: 'relative',
  background: 'linear-gradient(to bottom, #076698, rgba(7, 102, 152, 0.9))',
  padding: '1.25rem',
  borderRadius: '1rem',
})

export const ViewPortWarningStyledMonitor = styled(Monitor)({
  width: '3.5rem',
  height: '3.5rem',
  color: 'white',
})

export const ViewPortWarningStyledContent = styled.div({
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
})

export const ViewPortWarningStyledTextSection = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
})

export const ViewPortWarningStyledHeading = styled.h1({
  fontSize: '1.5rem',
  lineHeight: '2rem',
  fontWeight: 600,
  color: '#1a202c',
})

export const ViewPortWarningStyledMessage = styled.p({
  color: '#4a5568',
})

export const ViewPortWarningStyledInfoBox = styled.div({
  background:
    'linear-gradient(to right, rgba(7, 102, 152, 0.05), rgba(7, 102, 152, 0.1), rgba(7, 102, 152, 0.05))',
  borderRadius: '1rem',
  padding: '1.5rem',
})

export const ViewPortWarningStyledInfoContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  color: '#076698',
})

export const ViewPortWarningStyledDot = styled.span({
  display: 'inline-block',
  width: '0.375rem',
  height: '0.375rem',
  borderRadius: '9999px',
  backgroundColor: '#076698',
})

export const ViewPortWarningStyledInfoText = styled.p({
  margin: 0,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: 500,
})

export const SAStatusContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

export const SABadgeContainer = styled.div({
  minWidth: '96px',
})

export const SAActionContainer = styled.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  width: '68px',
})

export const SAActionButton = styled.button({
  width: '30px',
  height: '30px',
  borderRadius: '50%',
  border: '1px solid currentColor',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#0D8653',
  backgroundColor: 'transparent',
  transition: 'background-color 0.2s, color 0.2s',
  '&:hover': {
    backgroundColor: '#f3f4f6',
    color: '#374151',
  },
})

export const SAHistoryButton = styled.button({
  width: '30px',
  height: '30px',
  borderRadius: '50%',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#808080',
  backgroundColor: 'transparent',
  transition: 'background-color 0.2s, color 0.2s',
  '&:hover': {
    backgroundColor: '#f3f4f6',
    color: '#374151',
  },
})

export const StyledPopover = styled(Popover)`
  max-width: 200px;
`

export const SAOverlay = styled.div({
  position: 'fixed',
  inset: 0,
  zIndex: 10,
})

export const SAPopoverContent = styled.div({
  position: 'absolute',
  right: 0,
  marginTop: '8px',
  width: '192px',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow:
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  border: '1px solid #e5e7eb',
  padding: '12px',
  zIndex: 20,
})

export const SAPopoverGrid = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  width: '100%',
})

export const SAStatusButton = styled.button({
  width: 'fit-content',
  maxWidth: '100%',
  height: '29px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '6px',
  transition: 'background-color 0.2s',
  margin: '0 0 4px 0',
})

export const SAStatusBadgeWrapper = styled.span((props) => {
  const colorValue = props.color || '#6b7280'

  const isColorDark = (hex) => {
    const r = parseInt(hex.substring(1, 3), 16)
    const g = parseInt(hex.substring(3, 5), 16)
    const b = parseInt(hex.substring(5, 7), 16)

    const brightness = r * 0.299 + g * 0.587 + b * 0.114

    return brightness < 128
  }

  const isDark = isColorDark(colorValue)

  const convertHexToRGBA = (hex, opacity) => {
    const r = parseInt(hex.substring(1, 3), 16)
    const g = parseInt(hex.substring(3, 5), 16)
    const b = parseInt(hex.substring(5, 7), 16)

    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  }

  const bgColor = convertHexToRGBA(colorValue, 0.2)

  const textColor = isDark ? colorValue : darkenColor(colorValue, 40)

  function darkenColor(hex, percent) {
    const num = parseInt(hex.replace('#', ''), 16)
    const amt = Math.round(2.55 * -percent)
    const R = (num >> 16) + amt
    const G = ((num >> 8) & 0x00ff) + amt
    const B = (num & 0x0000ff) + amt

    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 0 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 0 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 0 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    )
  }

  return {
    width: '100%',
    height: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    borderRadius: '9999px',
    fontSize: '11px',
    fontWeight: 500,
    border: 'none',
    backgroundColor: bgColor,
    color: textColor,
  }
})

export const SAHistoryBadgeWrapper = styled(SAStatusBadgeWrapper)({
  width: 'auto',
  maxWidth: '100%',
  margin: '2px',
})

export const ConfigDetailsStyledCard = styled(Card)({
  background: 'white',
  borderRadius: '8px',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
  padding: '1.5rem',
  border: 'none',
})

export const ConfigDetailsCardHeader = styled('div')({
  marginBottom: '1.5rem',
  paddingBottom: '1rem',
  borderBottom: '1px solid #f0f0f0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

export const ConfigDetailsTitle = styled('h2')({
  fontSize: '16px',
  fontWeight: 600,
  margin: 0,
})

export const ConfigDetailsButtonContainer = styled('div')({
  display: 'flex',
  gap: '0.5rem',
})

export const ConfigDetailsLabel = styled('span')({
  fontSize: '14px',
  fontWeight: 500,
  color: '#5A5555',
  display: 'block',
  marginBottom: '4px',
})

export const ConfigDetailsValue = styled('span')({
  fontSize: '14px',
  fontWeight: 400,
  color: '#898384',
  display: 'block',
})

export const ConfigDetailsConfigRow = styled(Row)({
  marginBottom: '1rem',
  paddingBottom: '1rem',
  borderBottom: '1px solid #f0f0f0',
  '&:last-child': {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottom: 'none',
  },
})

export const TimePickerContainer = styled.div({
  position: 'relative',
  width: '100%',
})

export const TimeInput = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '0.5rem 0.625rem',
  backgroundColor: theme.white,
  border: '2px solid #e2e8f0',
  borderRadius: '6px',
  fontSize: '12px',
  lineHeight: '1.25rem',
  color: '#4a5568',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover:not(:disabled)': {
    borderColor: theme.brandColorOrange,
  },
  '&:focus': {
    outline: 'none',
    borderColor: theme.brandColorOrange,
  },
  '&:disabled': {
    backgroundColor: '#f7fafc',
    color: '#a0aec0',
    cursor: 'not-allowed',
  },
}))

export const IconWrapper = styled.span({
  display: 'flex',
  alignItems: 'center',
  color: '#a0aec0',
  transition: 'color 0.15s ease',
})

export const TimeText = styled.span({
  flex: 1,
  textAlign: 'left',
  marginLeft: '0.5rem',
  fontSize: '0.8125rem',
})

export const TimePickerPopoverContent = styled.div(({ theme }) => ({
  padding: '0.75rem',
  width: '190px',
  backgroundColor: theme.white,
  borderRadius: '0.25rem',
}))

export const TimePickerGrid = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

export const TimeUnitContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

export const TimeUnitButton = styled.button({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.5rem',
  height: '1.5rem',
  padding: 0,
  border: 'none',
  backgroundColor: 'transparent',
  color: '#4a5568',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover': {
    color: '#2d3748',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  '&:focus': {
    outline: 'none',
  },
})

export const TimeValueDisplay = styled.div({
  fontSize: '1rem',
  fontWeight: 500,
  padding: '0.25rem 0',
  minWidth: '1.875rem',
  textAlign: 'center',
  color: '#2d3748',
})

export const TimeSeparator = styled.div({
  fontSize: '1rem',
  fontWeight: 500,
  margin: '0 0.25rem',
  paddingTop: '0.25rem',
  color: '#4a5568',
})

export const PeriodSelectorContainer = styled.div({
  display: 'flex',
  overflow: 'hidden',
  height: '1.625rem',
  marginLeft: '0.5rem',
})

export const PeriodSelectorButton = styled.button(({active, theme }) => ({
  flex: 1,
  padding: '0.25rem 0.375rem',
  border: '1px solid transparent',
  borderBottom: active
    ? `2px solid ${theme.brandColorOrange}`
    : '1px solid transparent',
  backgroundColor: 'transparent',
  color: active ? theme.brandColorOrange : '#718096',
  fontSize: '0.6875rem',
  fontWeight: active ? 600 : 500,
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  minWidth: '2rem',
  '&:hover': {
    color: theme.brandColorOrange,
  },
  '&:focus': {
    outline: 'none',
  },
}))

export const TagInputContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  minHeight: '36px',
  height: 'auto',
  padding: '0.25rem 0.75rem',
  border: '1px solid #ced4da',
  borderRadius: '0.25rem',
  cursor: 'text',
  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
  backgroundColor: '#fff',

  '&:focus-within': {
    borderColor: '#86b7fe',
    boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)',
  },
})

export const TagInputTag = styled.div(({ isRtl }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.25rem',
  backgroundColor: '#E3E3E3',
  color: '#5A5555',
  borderRadius: '800px',
  padding: '2px 12px',
  margin: '0.1rem',
  maxHeight: '24px',
  fontSize: '12px',
  transition: 'all 0.2s linear',
  opacity: 1,
}))

export const TagInputCloseButton = styled.button({
  background: 'transparent',
  border: 'none',
  color: '#5A5555',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '14px',
  height: '14px',
  padding: 0,
  borderRadius: '50%',
  fontSize: '10px',
  opacity: 0.75,
  transition: 'all 0.2s ease',

  '&:hover': {
    opacity: 1,
    transform: 'scale(1.2)',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },

  '&::before': {
    content: '"×"',
    fontSize: '12px',
    fontWeight: 'bold',
    lineHeight: 1,
  },
})

export const TagInputField = styled.input({
  flex: '1 0 60px',
  border: 'none',
  outline: 'none',
  padding: '0.25rem',
  background: 'transparent',
  height: '24px',
  fontSize: '12px',
  margin: '0.25rem',

  '&::placeholder': {
    color: '#6c757d',
    opacity: 0.75,
  },
})

export const TagInputLabel = styled(Form.Label)({
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#212529bf',
  marginBottom: '0.25rem',
})

export const TimePickerAnimationStyles = `
  @keyframes slideUp {
    0% { transform: translateY(3px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes slideDown {
    0% { transform: translateY(-3px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.03); }
    100% { transform: scale(1); }
  }
`

export const EmailHistoryContainer = styled('div')({
  maxHeight: '600px',
  overflowY: 'auto',
  padding: '1rem',
})

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

export const EmailItemContainer = styled('div')(({ isNew }) => ({
  animation: isNew ? `${fadeInUp} 0.4s ease-out` : 'none',
}))

export const EmailContentContainer = styled('div')(({ shouldTruncate }) => ({
  maxHeight: shouldTruncate ? '100px' : 'none',
  overflow: shouldTruncate ? 'hidden' : 'visible',
  position: 'relative',
}))

export const EmailContentGradient = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '20px',
  background: 'linear-gradient(transparent, white)',
})

export const EmailStatusIcon = styled('div')(({ statusType, theme }) => ({
  width: '15px',
  height: '15px',
  marginLeft: '8px',
  color:
    statusType === 'sent'
      ? theme.success
      : statusType === 'failed'
        ? theme.danger
        : theme.brandColorOrange,
}))

export const EmailHeaderInfo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

export const LoadingContainer = styled('div')(
  ({ minHeight = 'auto', padding = '0' }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight,
    padding,
  }),
)

export const SpinnerWrapper = styled('div')(
  ({ size = '24px', borderWidth = '2px', theme }) => ({
    width: size,
    height: size,
    color: theme.brandColorOrange,
    borderWidth,
  }),
)

export const ShowMoreButton = styled(Button)({
  fontSize: '12px',
  padding: '0',
})

export const AttachmentButton = styled(Button)({
  padding: '0',
  marginLeft: '8px',
})

export const AttachmentIcon = styled('div')(({ theme }) => ({
  width: '18px',
  height: '18px',
  color: theme.brandColorOrange,
}))

export const LoadingSpinnerText = styled('div')(({ marginTop = '10px' }) => ({
  marginTop,
}))

export const TabsScrollContainer = styled('div')({
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'thin',
  WebkitOverflowScrolling: 'touch',
  marginBottom: '0.5rem',
  paddingBottom: '4px',

  '&::-webkit-scrollbar': {
    height: '6px',
    backgroundColor: 'transparent',
  },

  '&::-webkit-scrollbar-track': {
    background: '#f8f9fa',
    borderRadius: '8px',
    margin: '0 4px',
    border: '1px solid #e9ecef',
  },

  '&::-webkit-scrollbar-thumb': {
    background: 'linear-gradient(90deg, #dee2e6 0%, #ced4da 100%)',
    borderRadius: '8px',
    border: '1px solid #adb5bd',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    minWidth: '20px',
  },

  '&::-webkit-scrollbar-thumb:hover': {
    background: 'linear-gradient(90deg, #ced4da 0%, #adb5bd 100%)',
    borderColor: '#868e96',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transform: 'scaleY(1.2)',
  },

  '&::-webkit-scrollbar-thumb:active': {
    background: 'linear-gradient(90deg, #adb5bd 0%, #868e96 100%)',
    borderColor: '#6c757d',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
  },

  '&::-webkit-scrollbar-corner': {
    background: 'transparent',
  },

  scrollbarColor: '#ced4da #f8f9fa',
  scrollbarGutter: 'stable',

  scrollBehavior: 'smooth',

  '&:focus-within': {
    '&::-webkit-scrollbar-thumb': {
      borderColor: '#495057',
      boxShadow: '0 0 0 2px rgba(108, 117, 125, 0.2)',
    },
  },

  '@media (max-width: 768px)': {
    '&::-webkit-scrollbar': {
      height: '4px',
    },
    paddingBottom: '2px',
  },

  '@media (prefers-contrast: high)': {
    '&::-webkit-scrollbar-thumb': {
      background: '#495057',
      border: '1px solid #212529',
    },
    '&::-webkit-scrollbar-track': {
      background: '#ffffff',
      border: '1px solid #212529',
    },
  },
})

export const TabsStack = styled(Stack)({
  minWidth: 'fit-content',
  whiteSpace: 'nowrap',
  paddingBottom: '0.25rem',
  paddingRight: '0.5rem',
})

export const TabNavLink = styled(NavLink)({
  flexShrink: 0,
})

export const EmptyStateIcon = styled('div')(({ size = '50px', theme }) => ({
  width: size,
  height: size,
  color: theme.brandColorOrange,
}))

export const CircleRatingX = styled('input')(
  ({ isSelected, disabled, theme }) => ({
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    textAlign: 'center',
    background: isSelected ? theme.brandColorLightOrange : theme.gray200,
    border: `2px solid ${isSelected ? theme.brandColorOrange : theme.gray400}`,
    color: isSelected ? `${theme.white}` : theme.gray500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.3s, border 0.3s, color 0.3s',
    fontSize: '11px',
    fontWeight: 600,

    '&:hover': {
      // eslint-disable-next-line no-nested-ternary
      background: disabled
        ? null
        : isSelected
          ? `${theme.circleRatingBg}`
          : `${theme.gray300}`,
      border: disabled
        ? null
        : `2px solid ${isSelected ? `${theme.circleRatingBg}` : `${theme.gray500}`}`,
    },
  }),
)

export const MiniLogo = styled(CustomLogo)(
  ({ theme, primaryColor = null, secondaryColor = null }) => ({
    '& .logo-part1': {
      fill: primaryColor || theme.brandColorOrange,
    },
    '& .logo-part2': {
      fill: secondaryColor || theme.logoFill,
    },
  }),
)

export const StyledActivePositions = styled(ActivePositions)(({ theme }) => ({
  '& .outer-color': {
    fill: theme.steelBlue,
  },
  '& .inner-color': {
    fill: theme.blueSapphire,
  },
  '& .inner-border-color': {
    fill: theme.paleCerulean,
  },
  '& .main-color': {
    fill: theme.brandColorOrange,
  },
}))

export const StyledOrganizations = styled(Organizations)(({ theme }) => ({
  '& .secondary-color': {
    fill: theme.paleCerulean,
  },
  '& .main-color': {
    fill: theme.steelBlue,
  },
}))

export const TabSwtichContainer = styled.div(props => ({
  display: 'inline-flex',
  gap: 0,
  borderBottom: `2px solid ${props.theme.gray200}`
}))

export const TabSwitch = styled.button(props => ({
  padding: '8px 20px',
  fontSize: '13px',
  fontWeight: props.isActive ? 600 : 400,
  border: 'none',
  borderBottom: '2px solid transparent',
  backgroundColor: 'transparent',
  color: props.isActive ? props.theme.brandColorOrange : props.theme.gray600,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  whiteSpace: 'nowrap',
  marginBottom: '-2px',
  position: 'relative',
  
  ...(props.isActive && {
    borderBottomColor: props.theme.brandColorOrange
  }),
  
  '&:hover': {
    color: props.isActive ? props.theme.brandColorOrange : props.theme.brandColorDarkOrange
  },
  
  '&:focus': {
    outline: 'none'
  },
  
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5
  }
}))